import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { isImageUrlSquare, convertImageUrlToSquare, batchConvertToSquare, isImageSquare } from '../utils/imageConverter';

const MediaBrowser = ({ isOpen, onClose, onSelect, allowMultiple = true }) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState('browse'); // 'browse' or 'upload'
  const [hasNonSquareSelected, setHasNonSquareSelected] = useState(false);
  const [converting, setConverting] = useState(false);
  const [uploadFiles, setUploadFiles] = useState(null);
  const [hasNonSquareUploads, setHasNonSquareUploads] = useState(false);
  const [checkingDimensions, setCheckingDimensions] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMedia();
    }
  }, [isOpen, currentFolder]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/media/files', {
        params: currentFolder ? { folder_id: currentFolder.id } : {}
      });
      setFolders(response.data.folders || []);
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load media:', error);
      toast.error(error.response?.data?.detail || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadFiles(files);

    // Check if any uploaded files are non-square
    try {
      const squareChecks = await Promise.all(
        Array.from(files).map(file => isImageSquare(file))
      );
      setHasNonSquareUploads(squareChecks.some(isSquare => !isSquare));
    } catch (error) {
      console.error('Error checking upload dimensions:', error);
      setHasNonSquareUploads(false);
    }
  };

  const performUpload = async (filesToUpload) => {
    setUploading(true);
    const formData = new FormData();
    
    for (let file of filesToUpload) {
      formData.append('files', file);
    }

    try {
      const response = await axiosInstance.post(
        '/media/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const { uploaded, existing, failed } = response.data;
      
      if (failed > 0) {
        toast.warning(`Uploaded ${uploaded}, reused ${existing} existing, ${failed} failed`);
      } else if (existing > 0) {
        toast.success(`Uploaded ${uploaded} new files, reused ${existing} existing files`);
      } else {
        toast.success(`Successfully uploaded ${uploaded} file(s)`);
      }
      
      loadMedia();
      setView('browse');
      setUploadFiles(null);
      setHasNonSquareUploads(false);
    } catch (error) {
      console.error('Failed to upload files:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadWithoutConvert = () => {
    if (uploadFiles) {
      performUpload(Array.from(uploadFiles));
    }
  };

  const handleConvertAndUpload = async () => {
    if (!uploadFiles) return;

    setConverting(true);
    try {
      toast.info('Converting images to 1:1...');
      
      const convertedFiles = await batchConvertToSquare(Array.from(uploadFiles));
      
      toast.success('Images converted! Uploading...');
      await performUpload(convertedFiles);
      
    } catch (error) {
      console.error('Failed to convert images:', error);
      toast.error('Failed to convert images');
      setConverting(false);
    }
  };

  const toggleFileSelection = (file) => {
    // Get the file URL (GHL returns different field names)
    const fileUrl = file.url || file.fileUrl || file.hosted_url;
    
    if (allowMultiple) {
      setSelectedFiles(prev => 
        prev.find(f => (f.url || f.fileUrl || f.hosted_url) === fileUrl)
          ? prev.filter(f => (f.url || f.fileUrl || f.hosted_url) !== fileUrl)
          : [...prev, { ...file, url: fileUrl }]
      );
    } else {
      setSelectedFiles([{ ...file, url: fileUrl }]);
    }
  };

  // Check if selected files have non-square images
  useEffect(() => {
    const checkSquareImages = async () => {
      if (selectedFiles.length === 0) {
        setHasNonSquareSelected(false);
        setCheckingDimensions(false);
        return;
      }

      setCheckingDimensions(true);
      try {
        const squareChecks = await Promise.all(
          selectedFiles.map(file => {
            const url = file.url || file.fileUrl || file.hosted_url;
            return isImageUrlSquare(url);
          })
        );
        
        // If any image is not square, show the convert button
        setHasNonSquareSelected(squareChecks.some(isSquare => !isSquare));
      } catch (error) {
        console.error('Error checking image dimensions:', error);
        setHasNonSquareSelected(false);
      } finally {
        setCheckingDimensions(false);
      }
    };

    checkSquareImages();
  }, [selectedFiles]);

  const handleConvertSelectedToSquare = async () => {
    setConverting(true);
    try {
      toast.info('Converting images to 1:1...');
      
      // Prepare file data with URL, ID, and name
      const filesInfo = selectedFiles.map(file => ({
        url: file.url || file.fileUrl || file.hosted_url,
        id: file.id,
        name: file.name
      }));
      
      // Call backend to convert, upload, and delete originals
      const formData = new FormData();
      formData.append('file_data', JSON.stringify(filesInfo));

      const response = await axiosInstance.post('/media/convert-and-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { uploaded, failed, deleted, results } = response.data;
      
      if (failed > 0) {
        toast.warning(`Converted ${uploaded} images, ${failed} failed. Deleted ${deleted} originals.`);
      } else {
        toast.success(`Successfully converted ${uploaded} image(s) and replaced originals!`);
      }
      
      // Reload media to show updated images
      await loadMedia();
      
      // Auto-select the newly converted images
      const newSelectedFiles = results
        .filter(r => r.status === 'success')
        .map(r => ({
          url: r.new_url,
          id: r.new_id,
          name: r.new_url.split('/').pop()
        }));
      
      setSelectedFiles(newSelectedFiles);
      setHasNonSquareSelected(false);
      
    } catch (error) {
      console.error('Failed to convert images:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert images');
    } finally {
      setConverting(false);
    }
  };

  const handleSelect = () => {
    if (selectedFiles.length > 0) {
      onSelect(allowMultiple ? selectedFiles.map(f => f.url) : selectedFiles[0].url);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg shadow-xl w-[90vw] h-[80vh] max-w-6xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold">Excalibur Media</h2>
            {currentFolder && (
              <span className="text-slate-500">
                / {currentFolder.name}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={view === 'browse' ? 'default' : 'outline'}
              onClick={() => setView('browse')}
              size="sm"
            >
              Browse
            </Button>
            <Button
              variant={view === 'upload' ? 'default' : 'outline'}
              onClick={() => setView('upload')}
              size="sm"
            >
              Upload
            </Button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        {currentFolder && (
          <div className="px-6 py-3 border-b bg-slate-50">
            <button
              onClick={() => setCurrentFolder(null)}
              className="text-indigo-600 hover:text-indigo-800 text-sm"
            >
              ← Back to all folders
            </button>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {view === 'browse' ? (
            <>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <>
                  {/* Folders */}
                  {folders.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4">Folders</h3>
                      <div className="grid grid-cols-6 gap-4">
                        {folders.map((folder) => (
                          <button
                            key={folder.id}
                            onClick={() => setCurrentFolder(folder)}
                            className="flex flex-col items-center p-4 border-2 border-slate-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition"
                          >
                            <div className="text-5xl mb-2">📁</div>
                            <span className="text-sm text-center truncate w-full">
                              {folder.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {files.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Files</h3>
                      <div className="grid grid-cols-5 gap-4">
                        {files.map((file) => {
                          const fileUrl = file.url || file.fileUrl || file.hosted_url;
                          const isSelected = selectedFiles.find(f => (f.url || f.fileUrl || f.hosted_url) === fileUrl);
                          return (
                            <div
                              key={file.id || fileUrl}
                              onClick={() => toggleFileSelection(file)}
                              className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition ${
                                isSelected
                                  ? 'border-indigo-600 ring-2 ring-indigo-400'
                                  : 'border-slate-200 hover:border-indigo-300'
                              }`}
                            >
                              {fileUrl && fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img
                                  src={fileUrl}
                                  alt={file.name}
                                  className="w-full h-32 object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3E?%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-32 bg-slate-100 flex items-center justify-center">
                                  <span className="text-4xl">📄</span>
                                </div>
                              )}
                              <div className="p-2 bg-white">
                                <p className="text-xs truncate">{file.name}</p>
                              </div>
                              {isSelected && (
                                <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center">
                                  ✓
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {folders.length === 0 && files.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <div className="text-6xl mb-4">📭</div>
                      <p>No media files found</p>
                      <Button
                        onClick={() => setView('upload')}
                        className="mt-4"
                      >
                        Upload Files
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            /* Upload View */
            <div className="flex flex-col items-center justify-center h-full">
              <div className="max-w-md w-full">
                <h3 className="text-xl font-semibold mb-4 text-center">
                  Upload to Excalibur
                </h3>
                {currentFolder && (
                  <p className="text-sm text-slate-600 mb-4 text-center">
                    Uploading to: <strong>{currentFolder.name}</strong>
                  </p>
                )}
                
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading || converting}
                    className="hidden"
                    id="media-upload"
                  />
                  <label
                    htmlFor="media-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <div className="text-6xl mb-4">☁️</div>
                    <p className="text-lg font-medium mb-2">
                      {uploading ? 'Uploading...' : converting ? 'Converting...' : 'Choose files to upload'}
                    </p>
                    <p className="text-sm text-slate-500">
                      or drag and drop files here
                    </p>
                  </label>
                </div>

                {uploadFiles && uploadFiles.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <p className="text-sm text-slate-600 text-center">
                      {uploadFiles.length} file(s) ready to upload
                    </p>
                    <div className="flex gap-2 justify-center">
                      {hasNonSquareUploads && (
                        <Button
                          onClick={handleConvertAndUpload}
                          disabled={uploading || converting}
                          variant="secondary"
                          className="flex-1"
                        >
                          {converting ? 'Converting...' : 'Convert to 1:1 & Upload'}
                        </Button>
                      )}
                      <Button
                        onClick={handleUploadWithoutConvert}
                        disabled={uploading || converting}
                        className="flex-1"
                      >
                        {uploading ? 'Uploading...' : 'Upload As-Is'}
                      </Button>
                    </div>
                  </div>
                )}

                {!currentFolder && (
                  <p className="text-xs text-slate-500 mt-4 text-center">
                    Files will be uploaded to the "ProductFlow" folder
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-slate-50">
          <div className="text-sm text-slate-600">
            {selectedFiles.length > 0 && (
              <span>{selectedFiles.length} file(s) selected</span>
            )}
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {hasNonSquareSelected && view === 'browse' && (
              <Button
                onClick={handleConvertSelectedToSquare}
                disabled={converting || checkingDimensions}
                variant="secondary"
              >
                {checkingDimensions ? 'Checking...' : converting ? 'Converting...' : 'Convert to 1:1'}
              </Button>
            )}
            <Button
              onClick={handleSelect}
              disabled={selectedFiles.length === 0}
            >
              Select {selectedFiles.length > 0 && `(${selectedFiles.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaBrowser;
